import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { fnbApi } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import {
  ChefHat, Plus, Trash2, Utensils, TrendingDown, Percent, DollarSign,
  AlertTriangle, Pencil, Loader2,
} from 'lucide-react';

const ETB = (n) => `ETB ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const CATEGORIES = ['food', 'beverage', 'amenity'];

function foodCostTone(pct) {
  if (pct == null) return 'secondary';
  if (pct >= 40) return 'destructive';
  if (pct >= 30) return 'default';
  return 'default';
}

export default function FnbCostControlPage() {
  const { currentProperty } = useAuth();
  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [waste, setWaste] = useState([]);
  const [loading, setLoading] = useState(true);

  const [recipeDialog, setRecipeDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [wasteDialog, setWasteDialog] = useState(false);

  const pid = currentProperty?.id;

  const loadAll = useCallback(async () => {
    if (!pid) return;
    try {
      const [d, r, ing, w] = await Promise.all([
        fnbApi.dashboard(pid, 30),
        fnbApi.listRecipes(pid),
        fnbApi.listIngredients(pid),
        fnbApi.listWaste(pid),
      ]);
      setDashboard(d.data);
      setRecipes(r.data || []);
      setIngredients(ing.data || []);
      setWaste(w.data || []);
    } catch (e) {
      toast.error('Failed to load F&B data');
    } finally {
      setLoading(false);
    }
  }, [pid]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openNewRecipe = () => { setEditing(null); setRecipeDialog(true); };
  const openEditRecipe = async (id) => {
    try {
      const res = await fnbApi.getRecipe(id);
      setEditing(res.data);
      setRecipeDialog(true);
    } catch (e) { toast.error('Failed to load recipe'); }
  };
  const removeRecipe = async (id) => {
    try { await fnbApi.deleteRecipe(id); toast.success('Recipe deleted'); loadAll(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  if (!currentProperty) {
    return <div className="p-6"><Card><CardContent className="p-8 text-center text-slate-500">Select a property to manage F&amp;B cost control.</CardContent></Card></div>;
  }

  const dRecipes = dashboard?.recipes || {};
  const dWaste = dashboard?.wastage || {};

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-orange-500 text-white flex items-center justify-center">
            <ChefHat className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">F&amp;B Cost Control</h1>
            <p className="text-sm text-slate-500">Recipe costing, pricing accuracy &amp; wastage</p>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="recipes">Recipes &amp; Costing</TabsTrigger>
          <TabsTrigger value="waste">Wastage</TabsTrigger>
        </TabsList>

        {/* DASHBOARD */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card><CardContent className="p-4"><div className="flex items-center gap-3"><Utensils className="h-5 w-5 text-slate-500" /><div><p className="text-xs text-slate-500">Recipes</p><p className="text-xl font-semibold">{dRecipes.total || 0}</p></div></div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center gap-3"><Percent className="h-5 w-5 text-blue-500" /><div><p className="text-xs text-slate-500">Avg Food Cost</p><p className="text-xl font-semibold">{dRecipes.avg_food_cost_percent || 0}%</p></div></div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center gap-3"><DollarSign className="h-5 w-5 text-emerald-500" /><div><p className="text-xs text-slate-500">Avg Margin</p><p className="text-xl font-semibold">{ETB(dRecipes.avg_margin)}</p></div></div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center gap-3"><TrendingDown className="h-5 w-5 text-red-500" /><div><p className="text-xs text-slate-500">Wastage (30d)</p><p className="text-xl font-semibold">{ETB(dWaste.total_cost)}</p></div></div></CardContent></Card>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> High Food-Cost Recipes (&ge;35%)</CardTitle></CardHeader>
              <CardContent>
                {(dRecipes.high_food_cost || []).length === 0 ? (
                  <p className="text-sm text-slate-500">None — margins look healthy.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Recipe</TableHead><TableHead className="text-right">Food Cost</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {dRecipes.high_food_cost.map((r, i) => (
                        <TableRow key={i}><TableCell>{r.name}</TableCell><TableCell className="text-right"><Badge variant="destructive">{r.food_cost_percent}%</Badge></TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-sm flex items-center gap-2"><TrendingDown className="h-4 w-4 text-red-500" /> Top Wasted Items (30d)</CardTitle></CardHeader>
              <CardContent>
                {(dWaste.top_items || []).length === 0 ? (
                  <p className="text-sm text-slate-500">No wastage recorded.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Cost</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {dWaste.top_items.map((w, i) => (
                        <TableRow key={i}><TableCell>{w.item}</TableCell><TableCell className="text-right">{ETB(w.cost)}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* RECIPES */}
        <TabsContent value="recipes" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={openNewRecipe} className="bg-orange-500 hover:bg-orange-600">
              <Plus className="h-4 w-4 mr-2" /> New Recipe
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recipe</TableHead><TableHead>Category</TableHead>
                    <TableHead className="text-right">Cost / Unit</TableHead>
                    <TableHead className="text-right">Selling Price</TableHead>
                    <TableHead className="text-right">Food Cost</TableHead>
                    <TableHead className="text-right">Margin</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
                  ) : recipes.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">No recipes yet. Create one to start costing.</TableCell></TableRow>
                  ) : recipes.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell><Badge variant="outline" className="capitalize">{r.category}</Badge></TableCell>
                      <TableCell className="text-right">{ETB(r.cost_per_unit)}</TableCell>
                      <TableCell className="text-right">{ETB(r.selling_price)}</TableCell>
                      <TableCell className="text-right">
                        {r.food_cost_percent != null ? <Badge variant={foodCostTone(r.food_cost_percent)}>{r.food_cost_percent}%</Badge> : '—'}
                      </TableCell>
                      <TableCell className="text-right font-medium text-emerald-600">{ETB(r.margin)}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEditRecipe(r.id)}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => removeRecipe(r.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WASTE */}
        <TabsContent value="waste" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setWasteDialog(true)} variant="outline">
              <TrendingDown className="h-4 w-4 mr-2" /> Record Wastage
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Date</TableHead><TableHead>Item</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Cost</TableHead><TableHead>Reason</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {waste.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">No wastage recorded.</TableCell></TableRow>
                  ) : waste.map((w) => (
                    <TableRow key={w.id}>
                      <TableCell>{w.movement_date}</TableCell>
                      <TableCell>{w.inventory_items?.name || '—'}</TableCell>
                      <TableCell className="text-right">{w.quantity} {w.inventory_items?.unit_of_measure || ''}</TableCell>
                      <TableCell className="text-right text-red-600">{ETB(w.total_cost)}</TableCell>
                      <TableCell className="text-sm text-slate-500">{w.reason || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <RecipeDialog
        open={recipeDialog}
        onOpenChange={setRecipeDialog}
        propertyId={pid}
        ingredients={ingredients}
        editing={editing}
        onSaved={() => { setRecipeDialog(false); loadAll(); }}
      />
      <WasteDialog
        open={wasteDialog}
        onOpenChange={setWasteDialog}
        propertyId={pid}
        ingredients={ingredients}
        onSaved={() => { setWasteDialog(false); loadAll(); }}
      />
    </div>
  );
}

function RecipeDialog({ open, onOpenChange, propertyId, ingredients, editing, onSaved }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('food');
  const [outputQty, setOutputQty] = useState('1');
  const [outputUnit, setOutputUnit] = useState('portion');
  const [sellingPrice, setSellingPrice] = useState('');
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (editing) {
        setName(editing.name || '');
        setCategory(editing.category || 'food');
        setOutputQty(String(editing.output_quantity || 1));
        setOutputUnit(editing.output_unit || 'portion');
        setSellingPrice(String(editing.selling_price || ''));
        setLines((editing.ingredients || []).map((i) => ({
          item_id: i.item_id, quantity: String(i.quantity), waste_percent: String(i.waste_percent || 0),
        })));
      } else {
        setName(''); setCategory('food'); setOutputQty('1'); setOutputUnit('portion');
        setSellingPrice(''); setLines([]);
      }
    }
  }, [open, editing]);

  const costOf = (id) => (ingredients.find((x) => x.id === id)?.unit_cost || 0);
  const liveCost = lines.reduce((sum, l) => {
    const q = parseFloat(l.quantity) || 0;
    const w = parseFloat(l.waste_percent) || 0;
    return sum + q * (1 + w / 100) * costOf(l.item_id);
  }, 0);
  const outQ = parseFloat(outputQty) || 1;
  const costPerUnit = liveCost / (outQ > 0 ? outQ : 1);
  const price = parseFloat(sellingPrice) || 0;
  const foodPct = price > 0 ? (costPerUnit / price) * 100 : null;
  const margin = price - costPerUnit;

  const addLine = () => setLines([...lines, { item_id: '', quantity: '1', waste_percent: '0' }]);
  const updateLine = (i, field, val) => setLines(lines.map((l, idx) => (idx === i ? { ...l, [field]: val } : l)));
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!name.trim()) { toast.error('Enter a recipe name'); return; }
    const validLines = lines.filter((l) => l.item_id);
    const payload = {
      name, category,
      output_quantity: parseFloat(outputQty) || 1,
      output_unit: outputUnit,
      selling_price: parseFloat(sellingPrice) || 0,
      ingredients: validLines.map((l) => ({
        item_id: l.item_id,
        quantity: parseFloat(l.quantity) || 0,
        waste_percent: parseFloat(l.waste_percent) || 0,
      })),
    };
    try {
      setSaving(true);
      if (editing) await fnbApi.updateRecipe(editing.id, payload);
      else await fnbApi.createRecipe(propertyId, payload);
      toast.success(editing ? 'Recipe updated' : 'Recipe created');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save recipe');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Recipe' : 'New Recipe'}</DialogTitle>
          <DialogDescription>Ingredient costs roll up from inventory prices automatically.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Recipe Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Espresso" /></div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Output Qty</Label><Input type="number" value={outputQty} onChange={(e) => setOutputQty(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Output Unit</Label><Input value={outputUnit} onChange={(e) => setOutputUnit(e.target.value)} placeholder="portion / cup" /></div>
            <div className="space-y-1.5 col-span-2"><Label>Selling Price (ETB)</Label><Input type="number" value={sellingPrice} onChange={(e) => setSellingPrice(e.target.value)} placeholder="0.00" /></div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Ingredients</Label>
              <Button type="button" size="sm" variant="outline" onClick={addLine}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
            </div>
            {lines.length === 0 && <p className="text-xs text-slate-500">No ingredients added yet.</p>}
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-6">
                  <Select value={l.item_id} onValueChange={(v) => updateLine(i, 'item_id', v)}>
                    <SelectTrigger><SelectValue placeholder="Ingredient" /></SelectTrigger>
                    <SelectContent>
                      {ingredients.map((ing) => (
                        <SelectItem key={ing.id} value={ing.id}>{ing.name} ({ETB(ing.unit_cost)}/{ing.unit_of_measure})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Input type="number" value={l.quantity} onChange={(e) => updateLine(i, 'quantity', e.target.value)} placeholder="Qty" /></div>
                <div className="col-span-3"><Input type="number" value={l.waste_percent} onChange={(e) => updateLine(i, 'waste_percent', e.target.value)} placeholder="Waste %" /></div>
                <div className="col-span-1"><Button type="button" variant="ghost" size="icon" onClick={() => removeLine(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button></div>
              </div>
            ))}
          </div>

          {/* Live costing */}
          <div className="rounded-lg bg-slate-50 p-3 grid grid-cols-4 gap-2 text-center">
            <div><p className="text-[11px] text-slate-500">Cost / Unit</p><p className="font-semibold">{ETB(costPerUnit)}</p></div>
            <div><p className="text-[11px] text-slate-500">Food Cost</p><p className={`font-semibold ${foodPct != null && foodPct >= 40 ? 'text-red-600' : 'text-slate-800'}`}>{foodPct != null ? `${foodPct.toFixed(1)}%` : '—'}</p></div>
            <div><p className="text-[11px] text-slate-500">Margin</p><p className="font-semibold text-emerald-600">{ETB(margin)}</p></div>
            <div><p className="text-[11px] text-slate-500">Suggested @30%</p><p className="font-semibold">{ETB(costPerUnit / 0.30)}</p></div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Save Recipe
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WasteDialog({ open, onOpenChange, propertyId, ingredients, onSaved }) {
  const [itemId, setItemId] = useState('');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setItemId(''); setQty(''); setReason(''); } }, [open]);

  const item = ingredients.find((x) => x.id === itemId);
  const estCost = (parseFloat(qty) || 0) * (item?.unit_cost || 0);

  const save = async () => {
    if (!itemId) { toast.error('Select an item'); return; }
    if (!(parseFloat(qty) > 0)) { toast.error('Enter a quantity'); return; }
    try {
      setSaving(true);
      await fnbApi.recordWaste(propertyId, { item_id: itemId, quantity: parseFloat(qty), reason });
      toast.success('Wastage recorded');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record Wastage</DialogTitle>
          <DialogDescription>Track wasted stock and its cost impact.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Item</Label>
            <Select value={itemId} onValueChange={setItemId}>
              <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
              <SelectContent>
                {ingredients.map((ing) => <SelectItem key={ing.id} value={ing.id}>{ing.name} ({ETB(ing.unit_cost)}/{ing.unit_of_measure})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Quantity</Label><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="0" /></div>
          <div className="space-y-1.5"><Label>Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. spoilage, spillage" /></div>
          <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">Estimated cost: <b>{ETB(estCost)}</b></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} variant="destructive">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
